import { useCallback, useMemo, useState } from 'react';
import LanguageLocale from 'utils/LanguageLocale';
import DateUtils from 'utils/Date';
import ChangeDateModalEN from 'components/GlobalModals/ChangeDateModal/locales/ChangeDateModal_en_US';
import ChangeDateModalFR from 'components/GlobalModals/ChangeDateModal/locales/ChangeDateModal_fr_CA';

const { getLocalisedDataFromResource } = LanguageLocale;

const getText = getLocalisedDataFromResource(ChangeDateModalEN, ChangeDateModalFR);

/**
 * Custom hook for managing ChangeDateModal state and calendar logic
 * Calculates available dates and manages selected date state
 *
 * @param {object} config - Hook configuration
 * @param {Date} config.initialSelectedDate - Initial selected date
 * @returns {object} Hook return value
 * @returns {object} return.calendar - Calendar configuration object
 * @returns {Date[]} return.calendar.availableDays - Array of selectable dates
 * @returns {number} return.calendar.maxMonths - Inclusive number of calendar months spanned by the available dates
 * @returns {Date} return.selectedDate - Currently selected date
 * @returns {Function} return.changeSelectedDate - Callback to update selected date
 * @returns {object} return.localization - Localized text strings and weekday labels
 * @returns {string} return.nextShipmentDate - Formatted next shipment date string
 */
export default function useChangeDateModal({ initialSelectedDate }) {
    const [selectedDate, setSelectedDate] = useState(initialSelectedDate);

    const changeSelectedDate = useCallback(date => {
        setSelectedDate(date);
    }, []);

    const localization = useMemo(
        () => ({
            modalTitle: getText('modalTitle'),
            nextShipment: getText('nextShipment'),
            cancelButton: getText('cancelButton'),
            submitButton: getText('submitButton'),
            submitButtonSuccess: getText('submitButtonSuccess'),
            weekDays: DateUtils.getShortenedWeekdaysArray() // MON, TUE...
        }),
        []
    );

    const nextShipmentDate = initialSelectedDate && DateUtils.getPromiseDate(initialSelectedDate);

    const calendar = useMemo(() => {
        const changeDateConfig = typeof Sephora !== 'undefined' ? Sephora.configurationSettings?.autoReplenishmentConfigurations?.changeDate : null;

        const MAX_MONTH = Number(changeDateConfig?.maxNumberOfMonthsFromNowForNextDeliveryScheduleDate ?? 4);
        const MIN_DAYS_FOR_NEXT_DELIVERY = Number(changeDateConfig?.minNumberOfDaysFromNowForNextDeliveryScheduleDate ?? 2);

        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const startDate = DateUtils.clone(today);
        const endDate = DateUtils.addMonths(today, MAX_MONTH);

        let currentDate = DateUtils.clone(startDate);
        let daysFromToday = 0;

        const availableDays = [];

        while (currentDate <= endDate) {
            // Include only dates that are at least MIN_DAYS_FOR_NEXT_DELIVERY days from now
            if (daysFromToday >= MIN_DAYS_FOR_NEXT_DELIVERY) {
                availableDays.push(DateUtils.clone(currentDate));
            }

            const nextDay = DateUtils.clone(currentDate);
            nextDay.setDate(nextDay.getDate() + 1);
            currentDate = nextDay;
            daysFromToday++;
        }

        const maxMonths = availableDays.length > 0 ? DateUtils.getMonthDiff(today, availableDays[availableDays.length - 1]) + 1 : MAX_MONTH;

        return { availableDays, maxMonths };
    }, []);

    return {
        calendar,
        changeSelectedDate,
        localization,
        nextShipmentDate,
        selectedDate
    };
}
